import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ChevronDown, Check, Users } from 'lucide-react';
import { useUserStore } from '../../store/userStore';
import { decryptIfEncrypted } from '../../utils/encryption';
import ClubLogo from '../ClubLogo';
import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';
import { getClubNameApi } from '../../lib/clubsApi';
import { teamBelongsToSeason } from '../../store/seasonStore';
import SeasonIcon from '../SeasonIcon';

interface TeamSwitcherProps {
    collapsed?: boolean;
    seasonName?: string;
    seasonId?: string;
}

const TeamSwitcher: React.FC<TeamSwitcherProps> = ({ collapsed, seasonName, seasonId }) => {
    const { teams, currentUser, getCurrentTeam, setCurrentTeam } = useUserStore();
    const [isOpen, setIsOpen] = useState(false);
    const [decodedNames, setDecodedNames] = useState<Record<string, string>>({});
    const [clubName, setClubName] = useState<string>('');
    const dropdownRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();

    const currentTeam = getCurrentTeam();
    const activeTeamId = currentTeam?.id;
    const activeClubId = currentTeam?.club_id || currentUser?.club_id;

    // Filter teams where user is a coach
    const userTeams = useMemo(() => teams.filter(team =>
        team.coach.some(coach => coach.id === currentUser?.id)
        && teamBelongsToSeason(team, seasonId)
    ), [teams, currentUser?.id, seasonId]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        let cancelled = false;

        const decodeTeamNames = async () => {
            const entries = await Promise.all(userTeams.map(async (team) => {
                const clubId = team.club_id || currentUser?.club_id || '';
                if (!clubId) {
                    return [team.id, team.name] as const;
                }
                const decrypted = await decryptIfEncrypted({
                    value: team.name,
                    clubId
                });
                return [team.id, decrypted || team.name] as const;
            }));

            if (!cancelled) {
                const nextNames = Object.fromEntries(entries);
                setDecodedNames((currentNames) => {
                    const nextIds = Object.keys(nextNames);
                    const unchanged = nextIds.length === Object.keys(currentNames).length
                        && nextIds.every((teamId) => currentNames[teamId] === nextNames[teamId]);
                    return unchanged ? currentNames : nextNames;
                });
            }
        };

        void decodeTeamNames();
        return () => {
            cancelled = true;
        };
    }, [userTeams, currentUser?.club_id]);

    const sortedUserTeams = useMemo(() => {
        return [...userTeams].sort((a, b) => {
            const nameA = (decodedNames[a.id] || a.name || '').toString();
            const nameB = (decodedNames[b.id] || b.name || '').toString();
            return nameA.localeCompare(nameB, 'nl', { sensitivity: 'base', numeric: true });
        });
    }, [userTeams, decodedNames]);

    useEffect(() => {
        const fetchClubName = async () => {
            const clubId = currentTeam?.club_id || currentUser?.club_id;
            if (!clubId) {
                setClubName('');
                return;
            }

            try {
                const data = await getClubNameApi(clubId);
                if (data?.name) {
                    setClubName(data.name);
                }
            } catch {
                setClubName('');
            }
        };

        fetchClubName();
    }, [currentTeam?.club_id, currentUser?.club_id]);

    const handleSelectTeam = (teamId: string) => {
        setCurrentTeam(teamId);
        setIsOpen(false);
        navigate('/'); // Navigate to dashboard on team switch
    };

    const currentTeamName = activeTeamId ? (decodedNames[activeTeamId] || currentTeam?.name) : 'Select Team';

    if (sortedUserTeams.length === 0) {
        return null;
    }

    if (collapsed) {
        return (
            <div className="mb-2 flex justify-center">
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    className="h-10 w-10 rounded-lg bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
                    title={currentTeamName}
                >
                    {activeClubId ? <ClubLogo clubId={activeClubId} className="h-6 w-6" /> : <Users size={20} />}
                </button>
                {isOpen && (
                    <div className="absolute left-16 top-4 z-50 w-64 bg-white rounded-lg shadow-xl border border-gray-100 overflow-hidden">
                        <div className="py-2 px-3 bg-gray-50 border-b text-xs font-semibold text-gray-500 uppercase tracking-wider">
                            Selecteer Team
                        </div>
                        <div className="max-h-64 overflow-y-auto">
                            {sortedUserTeams.map(team => (
                                <button
                                    key={team.id}
                                    onClick={() => handleSelectTeam(team.id)}
                                    className="w-full text-left px-4 py-3 flex items-center hover:bg-gray-50 transition-colors"
                                >
                                    <div className="flex-grow">
                                        <div className={clsx("text-sm font-medium", activeTeamId === team.id ? "text-primary-600" : "text-gray-900")}>
                                            {decodedNames[team.id] || team.name}
                                        </div>
                                        {clubName && <div className="text-xs text-gray-500">{clubName}</div>}
                                    </div>
                                    {activeTeamId === team.id && <Check size={16} className="text-primary-600" />}
                                </button>
                            ))}
                            {sortedUserTeams.length === 0 && (
                                <div className="px-4 py-3 text-sm text-gray-500">Geen teams gevonden</div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="relative mb-2 px-2" ref={dropdownRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex items-center justify-between p-2 rounded-lg hover:bg-gray-100 transition-colors border border-transparent hover:border-gray-200"
            >
                <div className="flex items-center gap-3 overflow-hidden">
                    <div className="h-8 w-8 rounded bg-gray-100 flex items-center justify-center flex-shrink-0">
                        {activeClubId ? (
                            <ClubLogo clubId={activeClubId} className="h-6 w-6" />
                        ) : (
                            <Users size={16} />
                        )}
                    </div>
                    <div className="text-left overflow-hidden">
                        <div className="text-sm font-bold text-gray-900 truncate">
                            {currentTeamName}
                        </div>
                        <div className="text-xs text-gray-500 truncate">
                            {clubName ? `Club: ${clubName}` : 'Geen club geselecteerd'}
                        </div>
                        {seasonName && (
                            <div className="mt-0.5 flex items-center gap-1 text-xs font-medium text-gray-600">
                                <SeasonIcon size={12} className="flex-shrink-0 text-orange-500" />
                                <span className="truncate">{seasonName}</span>
                            </div>
                        )}
                    </div>
                </div>
                <ChevronDown size={16} className="text-gray-400 flex-shrink-0" />
            </button>

            {isOpen && (
                <div className="absolute top-full left-0 right-0 mt-1 z-50 bg-white rounded-lg shadow-xl border border-gray-100 overflow-hidden">
                    <div className="max-h-64 overflow-y-auto">
                        {sortedUserTeams.map(team => (
                            <button
                                key={team.id}
                                onClick={() => handleSelectTeam(team.id)}
                                className="w-full text-left px-4 py-3 flex items-center hover:bg-gray-50 transition-colors border-b last:border-0 border-gray-50"
                            >
                                <div className="flex-grow">
                                    <div className={clsx("text-sm font-medium", activeTeamId === team.id ? "text-primary-600" : "text-gray-900")}>
                                        {decodedNames[team.id] || team.name}
                                    </div>
                                </div>
                                {activeTeamId === team.id && <Check size={16} className="text-primary-600" />}
                            </button>
                        ))}
                        {sortedUserTeams.length === 0 && (
                            <div className="px-4 py-3 text-sm text-gray-500">Geen teams gevonden</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default TeamSwitcher;
